import { apiClient } from "./api";

const DISABLED_KEY = "foreducator:push-disabled";
let pending: Promise<void> = Promise.resolve();

export function supportsWebPush(): boolean {
  return typeof window !== "undefined" && window.isSecureContext &&
    "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

function disabled(): boolean {
  try { return localStorage.getItem(DISABLED_KEY) === "1"; } catch { return false; }
}

async function registration(): Promise<ServiceWorkerRegistration> {
  let timeout: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      navigator.serviceWorker.ready,
      new Promise<never>((_, reject) => {
        timeout = setTimeout(() => reject(new Error("알림 준비가 지연되고 있습니다. 페이지를 새로고침한 뒤 다시 시도해주세요.")), 10000);
      }),
    ]);
  } finally { clearTimeout(timeout); }
}

function serialize(action: () => Promise<void>): Promise<void> {
  const next = pending.then(action, action);
  pending = next.catch(() => {});
  return next;
}

function applicationKey(value: string): ArrayBuffer {
  const base64 = (value + "=".repeat((4 - value.length % 4) % 4)).replace(/-/g, "+").replace(/_/g, "/");
  return Uint8Array.from(atob(base64), c => c.charCodeAt(0)).buffer;
}

export type PushStatus = "unsupported" | "unconfigured" | "blocked" | "off" | "on";
export async function getWebPushStatus(): Promise<PushStatus> {
  if (!supportsWebPush()) return "unsupported";
  if (!import.meta.env.VITE_WEB_PUSH_PUBLIC_KEY?.trim()) return "unconfigured";
  if (Notification.permission === "denied") return "blocked";
  if (Notification.permission !== "granted" || disabled()) return "off";
  return await (await registration()).pushManager.getSubscription() ? "on" : "off";
}

// Background sync never opens a permission prompt. Only the explicit enable button does.
export function syncWebPushSubscription(): Promise<void> {
  return serialize(async () => {
    if (!supportsWebPush() || disabled() || Notification.permission !== "granted") return;
    const key = import.meta.env.VITE_WEB_PUSH_PUBLIC_KEY?.trim();
    if (!key) return;
    const reg = await registration();
    let subscription = await reg.pushManager.getSubscription();
    if (!subscription) {
      subscription = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: applicationKey(key) });
    }
    const data = subscription.toJSON();
    if (!data.endpoint || !data.keys?.p256dh || !data.keys?.auth) throw new Error("알림 구독 정보가 올바르지 않습니다.");
    await apiClient.upsertWebPushSubscription({ endpoint: data.endpoint, p256dh: data.keys.p256dh, authSecret: data.keys.auth, userAgent: navigator.userAgent });
  });
}

export async function enableWebPush(): Promise<void> {
  if (!supportsWebPush()) throw new Error("이 환경에서는 기기 알림을 지원하지 않습니다. Android에서는 최신 Chrome을 사용해주세요.");
  if (!import.meta.env.VITE_WEB_PUSH_PUBLIC_KEY?.trim()) throw new Error("알림 서버 설정을 확인해주세요.");
  // Must run directly in the click handler, before any network await.
  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error("알림이 허용되지 않았습니다. 휴대폰 설정 또는 사이트 설정에서 알림을 허용해주세요.");
  localStorage.removeItem(DISABLED_KEY);
  await syncWebPushSubscription();
}

export function deactivateCurrentWebPushSubscription(): Promise<void> {
  return serialize(async () => {
    if (!supportsWebPush()) return;
    const reg = await navigator.serviceWorker.getRegistration();
    const subscription = await reg?.pushManager.getSubscription();
    if (!subscription) return;
    let serverError: unknown;
    try { await apiClient.deactivateWebPushSubscription(subscription.endpoint); } catch (error) { serverError = error; }
    const removed = await subscription.unsubscribe();
    if (!removed && serverError) throw serverError;
  });
}

export async function disableWebPush(): Promise<void> {
  localStorage.setItem(DISABLED_KEY, "1");
  await deactivateCurrentWebPushSubscription();
}

export async function testWebPush(): Promise<void> {
  await syncWebPushSubscription();
  const subscription = await (await registration()).pushManager.getSubscription();
  if (!subscription) throw new Error("먼저 기기 알림을 켜주세요.");
  const response = await fetch("/api/notifications/test-push", {
    method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("access_token") || ""}` },
    body: JSON.stringify({ endpoint: subscription.endpoint }),
  });
  const result = await response.json();
  if (!response.ok) throw new Error(result.error || "테스트 알림을 보내지 못했습니다.");
}
